import { styled } from "@linaria/react"
import { Link } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"

import { Horizontal } from "@/components/Flex.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { pluralize } from "@/lib/displayUtils.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useUserTaskCounts } from "@/routes/user/UserTaskCountsModel.ts"
import { smallFontSize } from "@/styles/textStyles.ts"

import type { TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"

export const UserTaskCounts = observer((props: {
  profile: TD_MemberProfile
}) => {
  const context = useOrgContext()
  const taskCountsModel = useUserTaskCounts(context, props.profile)

  if (taskCountsModel.isLoading.get()) {
    return (
      <TaskCountsContainer>
        <TaskCountSkeleton />
      </TaskCountsContainer>
    )
  }

  if (taskCountsModel.total.get() === 0 && taskCountsModel.successful.get() === 0) {
    return (
      <EmptyTaskCountsContainer>
        Just getting started
      </EmptyTaskCountsContainer>
    )
  }

  return (
    <TaskCountsContainer>
      <TaskCountLink
        {...Navigator.user(context.orgId, props.profile.username)}
        viewTransition
      >
        {`${taskCountsModel.total.get()} ${pluralize("idea", taskCountsModel.total.get())}`}
      </TaskCountLink>
      {" | "}
      <TaskCountLink
        {...Navigator.user(context.orgId, props.profile.username)}
        search={{ successful: true }}
        viewTransition
      >
        {taskCountsModel.successful.get()} successful
      </TaskCountLink>
    </TaskCountsContainer>
  )
})

const TaskCountsContainer = styled(Horizontal)`
  align-items: center;
  gap: 4px;

  ${smallFontSize};

  color: var(--main-color-50);
`

const EmptyTaskCountsContainer = styled(TaskCountsContainer)`
  color: var(--secondary-text-color);
`

const TaskCountLink = styled(Link)`
  color: var(--secondary-text-color);

  &:hover {
    color: var(--main-color);
  }
`

const TaskCountSkeleton = styled.span`
  display: inline-block;
  position: relative;
  overflow: hidden;

  width: 90px;
  border-radius: 4px;
  background: var(--text-color-3);

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(100%);
    }
  }

  &::after {
    content: 'skeleton';
    visibility: hidden;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 60%) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
    will-change: transform;
  }
`